import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CloseRounded as CloseRoundedIcon,
  TuneOutlined as TuneIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import PageTitleIcon from "../PageTitleIcon";
import { APP_FONT_FAMILY } from "../../constants/fonts";
import {
  ESG_REPORT_SECTION_GROUPS,
  type EsgReportSectionId,
} from "../../constants/esgReportCustomize.catalog";

interface EsgReportCustomizeDialogProps {
  open: boolean;
  selectedSections: EsgReportSectionId[];
  onClose: () => void;
  onSave: (selectedSections: EsgReportSectionId[]) => void;
}

const EsgReportCustomizeDialog: React.FC<EsgReportCustomizeDialogProps> = ({
  open,
  selectedSections,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [draft, setDraft] = useState<EsgReportSectionId[]>(selectedSections);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open) {
      setDraft(selectedSections);
      setTab(0);
    }
  }, [open, selectedSections]);

  const activeGroup = ESG_REPORT_SECTION_GROUPS[tab] ?? ESG_REPORT_SECTION_GROUPS[0];
  const groupSelected = activeGroup.sections.filter((id) => draft.includes(id)).length;
  const groupAll = groupSelected === activeGroup.sections.length;

  const toggleSection = (id: EsgReportSectionId) => {
    setDraft((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleGroup = (sectionIds: readonly EsgReportSectionId[], checked: boolean) => {
    setDraft((prev) => {
      const without = prev.filter((id) => !sectionIds.includes(id));
      return checked ? [...without, ...sectionIds] : without;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.28 : 0.12)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 24px 64px ${alpha("#000", 0.55)}`
              : `0 20px 50px ${alpha("#0f172a", 0.16)}`,
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          position: "relative",
          px: { xs: 2.5, sm: 3 },
          pt: 2.75,
          pb: 0,
          fontSize: "inherit",
          fontWeight: "inherit",
          lineHeight: "inherit",
          letterSpacing: "inherit",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 55%, transparent 100%)`
              : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 55%, ${theme.palette.background.paper} 100%)`,
        }}
      >
        <IconButton
          aria-label={t("common.close")}
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            insetInlineEnd: 12,
            color: "text.secondary",
            bgcolor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.2 : 0.04),
            "&:hover": {
              bgcolor: alpha(
                theme.palette.common.black,
                theme.palette.mode === "dark" ? 0.32 : 0.08,
              ),
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pr: 5, pb: 2 }}>
          <PageTitleIcon icon={<TuneIcon />} size={48} />
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: APP_FONT_FAMILY,
                fontWeight: 800,
                fontSize: { xs: "1.2rem", sm: "1.35rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
                color: "text.primary",
              }}
            >
              {t("esgReports.customize")}
            </Typography>
            <Typography
              component="p"
              sx={{
                fontFamily: APP_FONT_FAMILY,
                mt: 0.5,
                fontWeight: 500,
                fontSize: "0.875rem",
                lineHeight: 1.45,
                color: "text.secondary",
              }}
            >
              {t("esgReports.customizeSubtitle")}
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="fullWidth"
          aria-label={t("esgReports.customize")}
          sx={{
            minHeight: 0,
            mx: { xs: -2.5, sm: -3 },
            borderTop: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.1)}`,
            "& .MuiTabs-indicator": { height: 3 },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontFamily: APP_FONT_FAMILY,
              fontSize: "0.8rem",
              lineHeight: 1.3,
              whiteSpace: "normal",
              minHeight: 56,
              maxWidth: "none",
              px: 1,
              py: 1,
            },
          }}
        >
          {ESG_REPORT_SECTION_GROUPS.map((group, index) => (
            <Tab
              key={group.id}
              id={`esg-customize-tab-${group.id}`}
              aria-controls={`esg-customize-panel-${group.id}`}
              value={index}
              label={t(`esgReports.customizeTabs.${group.id}`)}
            />
          ))}
        </Tabs>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{
          maxHeight: "55vh",
          px: { xs: 2.5, sm: 3 },
          pt: 2.5,
          pb: 1,
        }}
      >
        <Box
          role="tabpanel"
          id={`esg-customize-panel-${activeGroup.id}`}
          aria-labelledby={`esg-customize-tab-${activeGroup.id}`}
        >
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "10px",
              backgroundColor: theme.palette.action.hover,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>
              {t("esgReports.selectAllInThisReport")}
            </Typography>
            <Checkbox
              checked={groupAll}
              indeterminate={groupSelected > 0 && !groupAll}
              onChange={(event) => toggleGroup(activeGroup.sections, event.target.checked)}
              inputProps={{ "aria-label": t("esgReports.selectAllInThisReport") }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {activeGroup.sections.map((id) => {
              const checked = draft.includes(id);
              return (
                <Box
                  key={id}
                  onClick={() => toggleSection(id)}
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: checked
                      ? theme.palette.primary.main
                      : theme.palette.divider,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    cursor: "pointer",
                    opacity: checked ? 1 : 0.85,
                    boxShadow: checked
                      ? `0 0 0 2px ${theme.palette.primary.main}22`
                      : "none",
                    transition: "all 0.18s ease",
                    "&:hover": { opacity: 1 },
                  }}
                >
                  <Checkbox
                    checked={checked}
                    sx={{
                      p: 0,
                      "&.Mui-checked": { color: theme.palette.primary.main },
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, userSelect: "none" }}>
                    {t(`esgReports.glossary.sections.${id}.title`)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(
            theme.palette.primary.main,
            theme.palette.mode === "dark" ? 0.06 : 0.03,
          ),
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={() => onSave(draft)}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            px: 2.5,
            boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          {t("esgReports.saveChanges")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EsgReportCustomizeDialog;
